import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { accountsApi } from '@/services/accountingService';

interface Pagination {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

const initialPagination: Pagination = {
  currentPage: 1,
  lastPage: 1,
  perPage: 10,
  total: 0,
};

interface AccountsState {
  accounts: any[];
  account: any | null;
  pagination: Pagination;
  loading: boolean;
  error: any | null;
  /**
   * In-flight request count. A single boolean was cleared by whichever thunk
   * settled first, so a fetch running alongside a create/delete dropped the
   * spinner (and the disabled state) while the other request was still running.
   */
  pendingCount: number;
}

const initialState: AccountsState = {
  accounts: [],
  account: null,
  pagination: { ...initialPagination },
  loading: false,
  error: null,
  pendingCount: 0,
};

export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await accountsApi.getAll(params || {});
      // Guard the envelope: data.data may hold { accounts } or the array
      // itself, and may be missing entirely — never blind-dereference.
      const payload = response?.data?.data;
      const accounts = Array.isArray(payload?.accounts)
        ? payload.accounts
        : (Array.isArray(payload) ? payload : []);
      const meta = response?.data?.meta || {};
      return { accounts, meta };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAccount = createAsyncThunk(
  'accounts/fetchAccount',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response: any = await accountsApi.getById(id);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createAccount = createAsyncThunk(
  'accounts/createAccount',
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await accountsApi.create(data);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateAccount = createAsyncThunk(
  'accounts/updateAccount',
  async ({ id, data }: { id: number | string; data: any }, { rejectWithValue }) => {
    try {
      const response: any = await accountsApi.update(id, data);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'accounts/deleteAccount',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await accountsApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearAccount(state) {
      state.account = null;
    },
    resetAccounts(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    const start = (state: AccountsState) => {
      state.pendingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const done = (state: AccountsState) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
    };
    const fail = (state: AccountsState, action: any) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
      state.error = action.payload;
    };

    // fetchAccounts
    builder.addCase(fetchAccounts.pending, start);
    builder.addCase(fetchAccounts.fulfilled, (state, action) => {
      done(state);
      state.accounts = action.payload.accounts;
      const meta = action.payload.meta;
      state.pagination = {
        // `??` not `||`: a server reporting total 0 / perPage 0 was overridden
        // by the client defaults, and `meta.total || accounts.length` reported
        // the PAGE size as the total on page 2+.
        currentPage: meta.currentPage ?? 1,
        lastPage: meta.totalPages ?? meta.lastPage ?? 1,
        perPage: meta.perPage ?? 10,
        total: meta.total ?? state.accounts.length ?? 0,
      };
    });
    builder.addCase(fetchAccounts.rejected, fail);

    // fetchAccount
    builder.addCase(fetchAccount.pending, start);
    builder.addCase(fetchAccount.fulfilled, (state, action) => {
      done(state);
      state.account = action.payload;
    });
    builder.addCase(fetchAccount.rejected, fail);

    // createAccount
    builder.addCase(createAccount.pending, start);
    builder.addCase(createAccount.fulfilled, (state, action) => {
      done(state);
      state.accounts.unshift(action.payload);
      // Keep pagination in step with the list, otherwise the next fetch inherits
      // a total/page count that no longer matches the server.
      state.pagination.total += 1;
      state.pagination.lastPage = Math.max(
        state.pagination.lastPage,
        Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
      );
    });
    builder.addCase(createAccount.rejected, fail);

    // updateAccount
    builder.addCase(updateAccount.pending, start);
    builder.addCase(updateAccount.fulfilled, (state, action) => {
      done(state);
      const index = state.accounts.findIndex((a: any) => a.id === action.payload.id);
      if (index !== -1) {
        state.accounts[index] = action.payload;
      }
      if (state.account?.id === action.payload.id) {
        state.account = action.payload;
      }
    });
    builder.addCase(updateAccount.rejected, fail);

    // deleteAccount
    builder.addCase(deleteAccount.pending, start);
    builder.addCase(deleteAccount.fulfilled, (state, action) => {
      done(state);
      const nextAccounts = state.accounts.filter((a: any) => a.id !== action.payload);
      if (nextAccounts.length !== state.accounts.length) {
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.pagination.lastPage = Math.max(
          1,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      }
      state.accounts = nextAccounts;
      if (state.account?.id === action.payload) {
        state.account = null;
      }
    });
    builder.addCase(deleteAccount.rejected, fail);
  },
});

export const { clearError, clearAccount, resetAccounts } = accountsSlice.actions;

export const selectAccounts = (state: { accounts: AccountsState }) => state.accounts.accounts;
export const selectActiveAccounts = (state: { accounts: AccountsState }) => 
  state.accounts.accounts.filter((a: any) => a.isActive !== false && a.status !== 'inactive');
export const selectAccount = (state: { accounts: AccountsState }) => state.accounts.account;
export const selectAccountsLoading = (state: { accounts: AccountsState }) => state.accounts.loading;
export const selectAccountsError = (state: { accounts: AccountsState }) => state.accounts.error;
export const selectAccountsPagination = (state: { accounts: AccountsState }) => state.accounts.pagination;

export default accountsSlice.reducer;
